// Client-side helper for triggering branded app emails through the
// Lovable Emails send route. Requires a signed-in Supabase session; the
// route validates the bearer token and enqueues the email.
//
// Never throws — email failures must not break the calling user flow.
// All send errors are logged internally and swallowed.

import { supabase } from "@/integrations/supabase/client";

export type AppEmailTemplate =
  | "welcome"
  | "payment-receipt"
  | "invoice-uploaded"
  | "review-started"
  | "documents-required"
  | "review-complete"
  | "report-ready"
  | "negotiation-started"
  | "negotiation-update"
  | "negotiation-complete"
  | "auth-signup"
  | "auth-recovery"
  | "auth-magic-link"
  | "auth-invite"
  | "auth-email-change"
  | "auth-reauthentication";

export interface SendAppEmailInput {
  templateName: AppEmailTemplate;
  recipientEmail: string;
  /** Stable key so retries dedupe. */
  idempotencyKey: string;
  templateData?: Record<string, unknown>;
}

export interface SendAppEmailResult {
  ok: boolean;
  deduped?: boolean;
  reason?: string;
  status?: number;
}

export async function sendAppEmail(
  input: SendAppEmailInput,
): Promise<SendAppEmailResult> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.warn("[email] sendAppEmail called without session");
      return { ok: false, reason: "unauthenticated" };
    }

    const res = await fetch("/lovable/email/transactional/send", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        templateName: input.templateName,
        recipientEmail: input.recipientEmail,
        idempotencyKey: input.idempotencyKey,
        templateData: input.templateData ?? {},
      }),
    });

    const body = await res
      .json()
      .catch(() => ({}) as Record<string, unknown>);

    if (!res.ok) {
      console.error("[email] send failed", res.status, body);
      return { ok: false, status: res.status };
    }

    return {
      ok: true,
      deduped: Boolean((body as { deduped?: boolean }).deduped),
      reason: (body as { reason?: string }).reason,
    };
  } catch (err) {
    console.error("[email] send threw", err);
    return { ok: false, reason: "network_error" };
  }
}
